import type {
  WxCopilotApplicantContext,
  WxCopilotApplicantTableContext,
  WxCopilotOpeningContext,
  WxCopilotPageContext,
  WxCopilotWorkflowContext,
} from '@/types/wx-copilot-context';

type PageSpecificContext = Pick<
  WxCopilotPageContext,
  'workflow' | 'applicant' | 'applicantTable' | 'openings'
>;

class WxCopilotContextManager {
  private currentContext: WxCopilotPageContext | null = null;

  private pageSpecificContext: PageSpecificContext = {};

  private debounceTimer: NodeJS.Timeout | null = null;

  private retryTimer: NodeJS.Timeout | null = null;

  private readonly DEBOUNCE_DELAY = 500;

  private readonly RETRY_DELAY = 200;

  private readonly MAX_RETRIES = 10;

  private mountId = 'wx-copilot-root';

  sendPageContext(url: string, mountId = 'wx-copilot-root'): void {
    this.mountId = mountId;
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.flushPageContext(url);
    }, this.DEBOUNCE_DELAY);
  }

  setWorkflowContext(workflow: WxCopilotWorkflowContext | undefined): void {
    const changed =
      JSON.stringify(this.pageSpecificContext.workflow) !==
      JSON.stringify(workflow);
    this.pageSpecificContext.workflow = workflow;
    if (changed && this.currentContext) {
      this.sendContextUpdate();
    }
  }

  setApplicantContext(applicant: WxCopilotApplicantContext | undefined): void {
    const changed =
      JSON.stringify(this.pageSpecificContext.applicant) !==
      JSON.stringify(applicant);
    this.pageSpecificContext.applicant = applicant;
    if (changed && this.currentContext) {
      this.sendContextUpdate();
    }
  }

  setApplicantTableContext(
    applicantTable: WxCopilotApplicantTableContext | undefined,
  ): void {
    const changed =
      JSON.stringify(this.pageSpecificContext.applicantTable) !==
      JSON.stringify(applicantTable);
    this.pageSpecificContext.applicantTable = applicantTable;
    if (changed && this.currentContext) {
      this.sendContextUpdate();
    }
  }

  setOpeningsContext(openings: WxCopilotOpeningContext[] | undefined): void {
    const changed =
      JSON.stringify(this.pageSpecificContext.openings) !==
      JSON.stringify(openings);
    this.pageSpecificContext.openings = openings;
    if (changed && this.currentContext) {
      this.sendContextUpdate();
    }
  }

  private sendContextUpdate(): void {
    if (!this.currentContext) return;

    const fullContext: WxCopilotPageContext = {
      ...this.currentContext,
      ...this.pageSpecificContext,
      timestamp: Date.now(),
    };

    this.currentContext = fullContext;
    window.wxCopilotPageContext = fullContext;
    this.pushToCopilot(fullContext);
  }

  private flushPageContext(url: string): void {
    const newContext: WxCopilotPageContext = {
      url,
      title: typeof document !== 'undefined' ? document.title : undefined,
      timestamp: Date.now(),
      ...this.pageSpecificContext,
    };

    if (this.hasContextChanged(newContext)) {
      this.currentContext = newContext;
      window.wxCopilotPageContext = newContext;
      this.pushToCopilot(newContext);
    }
  }

  private pushToCopilot(context: WxCopilotPageContext, retryCount = 0): void {
    try {
      const copilotInstance =
        window.WxCopilotComponentInstances?.[this.mountId];

      if (copilotInstance?.updateEphemeralContext) {
        const contextPayload: Record<string, unknown> = {
          page: {
            url: context.url,
            title: context.title,
            timestamp: context.timestamp,
          },
        };

        if (context.workflow) {
          contextPayload.workflow = {
            id: context.workflow.workflowId,
            name: context.workflow.workflowName,
            currentStageId: context.workflow.currentStageId,
            currentStageName: context.workflow.currentStageName,
          };
        }

        if (context.applicant) {
          contextPayload.applicant = {
            id: context.applicant.applicantId,
            name: context.applicant.applicantName,
            email: context.applicant.applicantEmail,
          };
        }

        if (context.applicantTable) {
          contextPayload.applicantTable = {
            opening: {
              id: context.applicantTable.openingId,
              name: context.applicantTable.openingName,
            },
            currentStage: context.applicantTable.currentStageId
              ? {
                  id: context.applicantTable.currentStageId,
                  title: context.applicantTable.currentStageName,
                  type: context.applicantTable.currentStageType,
                }
              : undefined,
            stages: context.applicantTable.stages,
            applicants: context.applicantTable.applicants,
            totalApplicants: context.applicantTable.totalApplicants,
          };
        }

        if (context.openings && context.openings.length > 0) {
          contextPayload.openings = context.openings.map(opening => ({
            id: opening.id,
            name: opening.name,
          }));
        }

        (copilotInstance.updateEphemeralContext as (context: string) => void)(
          JSON.stringify(contextPayload),
        );
      } else if (retryCount < this.MAX_RETRIES) {
        if (this.retryTimer) {
          clearTimeout(this.retryTimer);
        }
        this.retryTimer = setTimeout(() => {
          this.pushToCopilot(context, retryCount + 1);
        }, this.RETRY_DELAY);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[WxCopilot] Error updating page context:', error as Error);
    }
  }

  private hasContextChanged(newContext: WxCopilotPageContext): boolean {
    if (!this.currentContext) {
      return true;
    }

    return (
      this.currentContext.url !== newContext.url ||
      JSON.stringify(this.currentContext.workflow) !==
        JSON.stringify(newContext.workflow) ||
      JSON.stringify(this.currentContext.applicant) !==
        JSON.stringify(newContext.applicant) ||
      JSON.stringify(this.currentContext.applicantTable) !==
        JSON.stringify(newContext.applicantTable) ||
      JSON.stringify(this.currentContext.openings) !==
        JSON.stringify(newContext.openings)
    );
  }

  clearPageSpecificContext(): void {
    this.pageSpecificContext = {};
  }

  cleanup(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }
}

export const wxCopilotContextManager = new WxCopilotContextManager();
